import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import type { RouterOutputs } from 'shared/api'
import { trpc, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { FacetedFilter } from 'shared/components/FacetedFilter'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { Textarea } from 'shared/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'
import { useLabel } from '../lib/use-label'

export type SupportDiscrepancy = RouterOutputs['discrepancies']['List'][number]

/** A support act on one Discrepancy: resolves on success, throws with a message on refusal. */
export type DiscrepancyAct = (discrepancyId: string, comment: string) => Promise<unknown>

const FILTERS = ['open', 'resolved', 'all'] as const
type Filter = (typeof FILTERS)[number]

const resolveViaTrpc: DiscrepancyAct = (discrepancyId, comment) =>
	trpcClient.discrepancies.Resolve.mutate({ discrepancyId, comment })
const declareLostViaTrpc: DiscrepancyAct = (discrepancyId, comment) =>
	trpcClient.discrepancies.DeclareLost.mutate({ discrepancyId, comment })

/**
 * «Расхождения» — support's first page (spec §19; ADR-0026, ADR-0025, ADR-0024): one
 * table of every Discrepancy, whatever found it, «Открытые / Решённые / Все» over it,
 * a search by code and a filter by kind. An open one is resolved in a dialog, always
 * with a comment the partner reads; «Объявить потерянной» — when the backend allows
 * it (`canDeclareLost`) — waits in the row's «⋯» and is confirmed, red. One whose
 * Parcel waits on the hub shelf is decided there.
 */
export function Discrepancies() {
	const { t } = useTranslate()
	const label = useLabel()
	const [status, setStatus] = useState<Filter>('open')
	const [search, setSearch] = useState('')
	const [kinds, setKinds] = useState<string[] | undefined>()
	const query = useQuery(trpc.discrepancies.List.queryOptions({ status }))

	const needle = search.trim().toLowerCase()
	const rows = useMemo(
		() =>
			(query.data ?? []).filter(
				(d) =>
					(!kinds?.length || kinds.includes(d.kind)) &&
					(!needle ||
						[d.parcel?.trackingCode, d.scannedCode, d.against.label, d.container?.code].some((code) =>
							code?.toLowerCase().includes(needle),
						)),
			),
		[query.data, kinds, needle],
	)
	const kindOptions = useMemo(
		() =>
			[...new Set((query.data ?? []).map((d) => d.kind))].map((kind) => ({
				value: kind,
				label: label(`discrepancyKind.${kind}`, kind),
			})),
		[query.data, label],
	)

	return (
		<div className='flex flex-col gap-3'>
			<div className='flex flex-wrap items-center gap-2'>
				<div role='tablist' className='inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5'>
					{FILTERS.map((f) => (
						<Button
							key={f}
							role='tab'
							size='sm'
							variant={status === f ? 'outline' : 'ghost'}
							aria-selected={status === f}
							onClick={() => setStatus(f)}
							data-testid={`discrepancy-filter-${f}`}
						>
							{t(`support.filter_${f}`)}
						</Button>
					))}
				</div>
				<div className='relative w-64'>
					<Search className='pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground' />
					<Input
						className='h-8 pl-8'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={t('support.search')}
						aria-label={t('support.search')}
					/>
				</div>
				<FacetedFilter title={t('support.colKind')} values={kinds} setValues={setKinds} options={kindOptions} />
			</div>
			{query.isError ? (
				<p className='text-sm text-destructive'>{query.error.message}</p>
			) : query.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('support.loading')}</p>
			) : (
				<DiscrepancyTable
					items={rows}
					resolve={resolveViaTrpc}
					declareLost={declareLostViaTrpc}
					onDone={() => query.refetch()}
				/>
			)}
		</div>
	)
}

type Act = { item: SupportDiscrepancy; mode: 'resolve' | 'lost' }

/**
 * The Discrepancies as one table, and the two acts on an open one: «Решить» in the row
 * (a dialog with the comment) and «Объявить потерянной» in its «⋯» (confirmed, red).
 * Both are refused here without a comment, as the backend refuses them. The acts are
 * injected so the table is driven at its props in `bun test`.
 */
export function DiscrepancyTable({
	items,
	resolve,
	declareLost,
	onDone,
}: {
	items: SupportDiscrepancy[]
	resolve: DiscrepancyAct
	declareLost: DiscrepancyAct
	onDone: () => void
}) {
	const { t } = useTranslate()
	const label = useLabel()
	const [act, setAct] = useState<Act | null>(null)

	const columns = useMemo<ColumnDef<SupportDiscrepancy>[]>(
		() => [
			{
				id: 'kind',
				header: t('support.colKind'),
				// The kind and a Box's weighing wrap rather than widen the table.
				meta: { cellClassName: 'whitespace-normal' },
				cell: ({ row: { original: d } }) => (
					<div className='flex max-w-44 flex-col gap-0.5'>
						<span className='font-medium' data-testid='discrepancy-kind'>
							{label(`discrepancyKind.${d.kind}`, d.kind)}
						</span>
						{d.boxWeight && (
							// A Box weighed beyond the tolerance: the weighing as it was at its intake (ADR-0047).
							<span className='text-xs text-muted-foreground' data-testid='discrepancy-box-weight'>
								{t('support.boxWeight', {
									hub: String(d.boxWeight.hubWeightKg),
									declared: String(d.boxWeight.declaredWeightKg),
									tolerance: String(d.boxWeight.tolerancePercent),
								})}
							</span>
						)}
					</div>
				),
			},
			{
				id: 'parcel',
				header: t('support.colParcel'),
				meta: { cellClassName: 'whitespace-normal' },
				cell: ({ row: { original: d } }) => <ParcelCell item={d} />,
			},
			{
				id: 'against',
				header: t('support.colAgainst'),
				cell: ({ row: { original: d } }) => (
					<div className='flex flex-col' data-testid='discrepancy-against'>
						<span className='font-mono'>
							{d.against.label || d.against.id}
							{d.container && d.container.code !== d.against.label && ` · ${d.container.code}`}
						</span>
						<span className='text-xs text-muted-foreground'>
							{label(`support.against_${d.against.type}`, d.against.type)}
							{d.container?.pointCode && (
								<span data-testid='discrepancy-container-point'> → {d.container.pointCode}</span>
							)}
						</span>
					</div>
				),
			},
			{
				id: 'responsible',
				header: t('support.responsible'),
				cell: ({ row: { original: d } }) =>
					d.responsible ? (
						<span data-testid='discrepancy-responsible' data-responsible={d.responsible}>
							{label(`support.responsible_${d.responsible}`, d.responsible)}
						</span>
					) : (
						<span className='text-muted-foreground'>—</span>
					),
			},
			{
				id: 'where',
				header: t('support.colWhere'),
				cell: ({ row: { original: d } }) => (
					<div className='flex flex-col'>
						<span>{d.locationCode ?? '—'}</span>
						<span className='text-xs text-muted-foreground tabular-nums' data-testid='discrepancy-at'>
							{formatDayTime(d.at)}
						</span>
					</div>
				),
			},
			{
				id: 'outcome',
				header: t('support.colOutcome'),
				meta: { cellClassName: 'whitespace-normal' },
				cell: ({ row: { original: d } }) => <Outcome item={d} />,
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row: { original: d } }) => <RowActions item={d} onAct={setAct} />,
			},
		],
		[t, label],
	)

	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (d) => d.discrepancyId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='discrepancies-empty'>{t('support.empty')}</span>}
				row={{
					attributes: (d) => ({
						'data-testid': `discrepancy-${d.discrepancyId}`,
						'data-kind': d.kind,
						'data-status': d.status,
					}),
				}}
			/>
			<ActDialog act={act} resolve={resolve} declareLost={declareLost} onClose={() => setAct(null)} onDone={onDone} />
		</>
	)
}

/** The Parcel the Discrepancy is about, as it is now; a Box's own weighing has none. */
function ParcelCell({ item }: { item: SupportDiscrepancy }) {
	const label = useLabel()
	const parcel = item.parcel
	// A Box weighed beyond the tolerance carries the Box's code as scanned: no Parcel for it.
	const code = item.boxWeight ? null : item.scannedCode
	const under = [item.partnerName, parcel && label(`parcelState.${parcel.state}`, parcel.state)].filter(Boolean).join(' · ')
	if (!parcel && !code)
		return <span className='text-muted-foreground'>{item.partnerName ?? '—'}</span>
	return (
		<div className='flex max-w-48 flex-col' data-testid='discrepancy-parcel'>
			{parcel ? (
				<Link to={`/parcels/${parcel.parcelId}`} className='font-mono underline-offset-4 hover:underline'>
					{parcel.trackingCode || code || parcel.parcelId}
				</Link>
			) : (
				<span className='font-mono'>{code}</span>
			)}
			{under && <span className='text-xs text-muted-foreground'>{under}</span>}
		</div>
	)
}

/** What became of it: lost, found, resolved — or, open, that its Parcel waits on the hub shelf. */
function Outcome({ item: d }: { item: SupportDiscrepancy }) {
	const { t } = useTranslate()
	return (
		<div className='flex flex-col items-start gap-1'>
			{d.found && (
				// «Снято: посылка найдена» — the Parcel declared lost from here turned up (ticket 43): where and when.
				<Commented
					testId='discrepancy-found'
					badge={<Badge variant='secondary'>{t('support.outcomeFound')}</Badge>}
					comment={[
						t(`foundWhere.${d.found.where}`),
						d.found.locationCode,
						formatDayTime(d.found.at),
					]
						.filter(Boolean)
						.join(' · ')}
				/>
			)}
			{d.lostDeclared && !d.found && (
				<Commented testId='discrepancy-lost' badge={<Badge variant='destructive'>{t('support.outcomeLost')}</Badge>} comment={d.lostDeclared.comment} />
			)}
			{d.resolution && !d.found && (
				<Commented testId='discrepancy-resolution' badge={<Badge variant='secondary'>{t('support.status_resolved')}</Badge>} comment={d.resolution.comment} />
			)}
			{d.status === 'open' && d.onHubShelf && (
				// Its Parcel waits on the hub shelf: support decides its fate there, which resolves this one (ticket 44).
				<Tooltip>
					<TooltipTrigger asChild>
						<Link
							to='/support/hub-shelf'
							className='text-sm underline underline-offset-4'
							data-testid='discrepancy-on-hub-shelf'
						>
							{t('support.onHubShelf')}
						</Link>
					</TooltipTrigger>
					<TooltipContent>{t('hubShelfDecision.decideThere')}</TooltipContent>
				</Tooltip>
			)}
		</div>
	)
}

/** A mark with its comment beside it, cut to one line; the whole comment on hover. */
function Commented({ testId, badge, comment }: { testId: string; badge: React.ReactNode; comment: string }) {
	return (
		<div className='flex max-w-52 items-center gap-2' data-testid={testId}>
			{badge}
			<Tooltip>
				<TooltipTrigger asChild>
					<span className='truncate text-sm text-muted-foreground'>{comment}</span>
				</TooltipTrigger>
				<TooltipContent>{comment}</TooltipContent>
			</Tooltip>
		</div>
	)
}

/** «Решить» for an open one not waiting on the hub shelf; «⋯» with its Parcel and, when allowed, «Объявить потерянной». */
function RowActions({ item, onAct }: { item: SupportDiscrepancy; onAct: (act: Act) => void }) {
	const { t } = useTranslate()
	const workable = item.status === 'open' && !item.onHubShelf
	const canLose = workable && item.canDeclareLost
	if (!workable && !item.parcel) return null
	return (
		<div className='flex items-center justify-end gap-1'>
			{workable && (
				<Button size='sm' variant='outline' onClick={() => onAct({ item, mode: 'resolve' })} data-testid='discrepancy-resolve'>
					{t('support.resolveOpen')}
				</Button>
			)}
			{(item.parcel || canLose) && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' size='icon-sm' aria-label={t('support.actions')} data-testid='discrepancy-menu'>
							<MoreHorizontal />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						{item.parcel && (
							<DropdownMenuItem asChild>
								<Link to={`/parcels/${item.parcel.parcelId}`}>{t('support.openParcel')}</Link>
							</DropdownMenuItem>
						)}
						{canLose && (
							<DropdownMenuItem
								variant='destructive'
								onSelect={() => onAct({ item, mode: 'lost' })}
								data-testid='discrepancy-declare-lost'
							>
								{t('support.declareLost')}
							</DropdownMenuItem>
						)}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	)
}

/**
 * The comment and the act: «Решено» in a dialog, «Объявить потерянной» in a red
 * confirmation. Without a comment nothing is sent; a refusal is the server's, in words.
 */
function ActDialog({
	act,
	resolve,
	declareLost,
	onClose,
	onDone,
}: {
	act: Act | null
	resolve: DiscrepancyAct
	declareLost: DiscrepancyAct
	onClose: () => void
	onDone: () => void
}) {
	const { t } = useTranslate()
	const label = useLabel()
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [seeded, setSeeded] = useState<Act | null>(null)
	if (act !== seeded) {
		setSeeded(act)
		setComment('')
		setError(null)
	}

	const submit = async () => {
		if (!act || busy) return
		const text = comment.trim()
		if (!text) {
			setError(t('support.commentRequired'))
			return
		}
		setBusy(true)
		setError(null)
		try {
			await (act.mode === 'resolve' ? resolve : declareLost)(act.item.discrepancyId, text)
			onClose()
			onDone()
		} catch (err) {
			setError(t('support.actionFailed', { message: err instanceof Error ? err.message : String(err) }))
		} finally {
			setBusy(false)
		}
	}

	const field = (
		<div className='grid gap-2'>
			<Textarea
				aria-label={t('support.comment')}
				placeholder={t('support.commentPlaceholder')}
				value={comment}
				onChange={(e) => setComment(e.target.value)}
				data-testid='discrepancy-comment'
			/>
			{error && (
				<p role='alert' className='text-sm text-destructive' data-testid='discrepancy-error'>
					{error}
				</p>
			)}
		</div>
	)
	const item = act?.item
	const what = item
		? [label(`discrepancyKind.${item.kind}`, item.kind), item.parcel?.trackingCode || item.against.label].filter(Boolean).join(' · ')
		: ''

	return (
		<>
			<Dialog open={act?.mode === 'resolve'} onOpenChange={(open) => !open && !busy && onClose()}>
				<DialogContent className='sm:max-w-lg' data-testid='discrepancy-resolve-dialog'>
					<DialogHeader>
						<DialogTitle>{t('support.resolveTitle')}</DialogTitle>
						<DialogDescription>{what}</DialogDescription>
					</DialogHeader>
					{field}
					<DialogFooter>
						<Button variant='outline' onClick={onClose} disabled={busy}>
							{t('support.cancel')}
						</Button>
						<Button onClick={submit} disabled={busy} data-testid='discrepancy-resolve-submit'>
							{t('support.resolve')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<AlertDialog open={act?.mode === 'lost'} onOpenChange={(open) => !open && !busy && onClose()}>
				<AlertDialogContent className='data-[size=default]:sm:max-w-lg' data-testid='discrepancy-lost-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('support.lostTitle', { code: item?.parcel?.trackingCode || item?.scannedCode || '' })}
						</AlertDialogTitle>
						<AlertDialogDescription>{t('support.lostHint')}</AlertDialogDescription>
					</AlertDialogHeader>
					{field}
					<AlertDialogFooter>
						<AlertDialogCancel disabled={busy}>{t('support.cancel')}</AlertDialogCancel>
						<Button variant='destructive' onClick={submit} disabled={busy} data-testid='discrepancy-declare-lost-submit'>
							{t('support.lostConfirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
